import ChairIcon from '@mui/icons-material/Chair';
import EditIcon from '@mui/icons-material/Edit';
import FenceIcon from '@mui/icons-material/Fence';
import PetsIcon from '@mui/icons-material/Pets';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import React from 'react';

interface AboutHomeCardProps {
  acceptedPets: {
    preferences: string[];
  };
  home: {
    hasFencedYard: boolean;
    allowsPetsOnFurniture: boolean;
  };
  sitterName: string;
  isOwnProfile?: boolean;
}

const AboutHomeCard: React.FC<AboutHomeCardProps> = ({
  acceptedPets,
  home,
  sitterName,
  isOwnProfile = false,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 2,
        p: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        mb: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          About {sitterName}'s Home
        </Typography>
        {isOwnProfile && (
          <IconButton size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Accepted Pets */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
          Accepted pets
        </Typography>
        {acceptedPets.preferences.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {acceptedPets.preferences.map((pref) => (
              <Chip
                key={pref}
                icon={<PetsIcon sx={{ fontSize: 16 }} />}
                label={pref.charAt(0).toUpperCase() + pref.slice(1).replace(/-/g, ' ')}
                sx={{
                  backgroundColor: '#E8F5F5',
                  color: '#178B8E',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#178B8E' },
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No pet preferences provided yet.
          </Typography>
        )}
      </Box>

      {/* Home Environment */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
          {sitterName}'s home
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {home.hasFencedYard && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FenceIcon sx={{ fontSize: 24, color: '#178B8E' }} />
              <Typography variant="body2">Fenced backyard</Typography>
            </Box>
          )}
          {home.allowsPetsOnFurniture && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ChairIcon sx={{ fontSize: 24, color: '#178B8E' }} />
              <Typography variant="body2">Pets allowed on furniture</Typography>
            </Box>
          )}
          {!home.hasFencedYard && !home.allowsPetsOnFurniture && (
            <Typography variant="body2" color="text.secondary">
              No home details provided yet.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AboutHomeCard;
